import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { Button, Pagination } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Custom Hooks & Services
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import subTaskService from 'app/services/subtask_service';

// Redux Actions
import { getVendorData, VendorDataCleanUp } from 'app/redux/actions/subTaskAction';

// Component
import VendorsForm from './VendorsForm';

// --- Constants for Readability and Maintenance ---
const PAGE_LIMIT = 10;
const INITIAL_PAGE_ID = 1;
const DEFAULT_PAGE = 1;

export default function VendorsMetadata() {
  const dispatch = useDispatch();
  const { setAuthToken } = useJumboAuth();
  const Swal = useSwalWrapper();
  const { vendorData } = useSelector(state => state.typereducer);
  const { hideDialog, showDialog } = useJumboDialog();

  // --- State Management with URL Search Params ---
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const filterParam = searchParams.get('filter');

  // Derive state from URL params
  const currentPage = parseInt(pageParam) || DEFAULT_PAGE;
  const filterStringFromUrl = filterParam || '';

  // Local state for non-URL-managed values
  const [totalCount, setTotalCount] = React.useState(0);
  const [queryOptions, setQueryOptions] = React.useState({});

  // Memoized values derived from current page
  const offset = (currentPage - 1) * PAGE_LIMIT;
  let pageIdCounter = offset + INITIAL_PAGE_ID;

  // --- SweetAlert Helper Function (Optimized for Clarity) ---
  const sweetAlerts = React.useCallback((variant, message, callback) => {
    Swal.fire({
      icon: variant,
      title: variant === "success" ? 'Success' : 'Warning',
      text: message,
      showCancelButton: variant !== "success",
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed && callback) {
        callback();
      }
    });
  }, [Swal]);

  // --- Data Fetching Effect (Core Logic) ---
  React.useEffect(() => {
    // 1. Determine filter string for API call
    const currentFilterString = queryOptions?.filterModel?.items
      ? queryOptions.filterModel.items
          .filter(item => item.field !== undefined)
          .map(item => `&${item.field}=${item.value || ''}`)
          .join('')
      : filterStringFromUrl; // Use URL filter string if no local filter options are set yet

    // 2. Perform API call
    subTaskService.getVendorData(offset, PAGE_LIMIT, currentFilterString)
      .then((data) => {
        setTotalCount(data?.data?.count || 0);
        dispatch(getVendorData(data?.data?.data || []));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setAuthToken(null);
        }
        console.error("Error fetching vendor data:", err);
      });

    // 3. Cleanup on unmount
    return () => {
      dispatch(VendorDataCleanUp());
    };
  }, [dispatch, setAuthToken, offset, queryOptions, filterStringFromUrl]);


  // --- Event Handlers ---

  const handlePageChange = React.useCallback((event, newPage) => {
    // Update URL query params for pagination
    setSearchParams(prevParams => {
      prevParams.set('page', newPage);
      return prevParams;
    }, { replace: true });
  }, [setSearchParams]);

  const onFilterChange = React.useCallback((filterModel) => {
    setQueryOptions({ filterModel });

    // Update URL query params for filtering
    const filterItems = filterModel?.items || [];
    const filterString = filterItems.length > 0
        ? JSON.stringify(filterItems.map(item => ({ field: item.field, value: item.value })))
        : '';

    setSearchParams(prevParams => {
        if (filterString) {
            prevParams.set('filter', filterString);
        } else {
            prevParams.delete('filter');
        }
        // Reset page to 1 when filter changes
        prevParams.set('page', DEFAULT_PAGE);
        return prevParams;
    }, { replace: true });
  }, [setSearchParams]);


  const handleVendorDataAdd = React.useCallback(() => {
    hideDialog();
    // Refresh data after successful add/edit (simple way: reload current page)
    const currentFilterString = queryOptions?.filterModel?.items
        ? queryOptions.filterModel.items
            .filter(item => item.field !== undefined)
            .map(item => `&${item.field}=${item.value || ''}`)
            .join('')
        : filterStringFromUrl;

    subTaskService.getVendorData(offset, PAGE_LIMIT, currentFilterString)
        .then((data) => {
            setTotalCount(data?.data?.count || 0);
            dispatch(getVendorData(data?.data?.data || []));
        })
        .catch((err) => {
             console.error("Error refreshing vendor data:", err);
        });
  }, [hideDialog, dispatch, offset, queryOptions, filterStringFromUrl]);

  const showAddVendorDialog = React.useCallback((vendorId) => {
    showDialog({
      title: vendorId ? "Edit Vendor Data" : "Add Vendor Data",
      content: <VendorsForm vendorId={vendorId} onSave={handleVendorDataAdd} />
    });
  }, [handleVendorDataAdd, showDialog]);

  const handleDeleteVendor = (vendorId) => {
    sweetAlerts("warning", "Are you sure you want to delete this vendor?", () => {
      subTaskService.deleteVendorData(vendorId)
        .then(() => {
          hideDialog();
          sweetAlerts('success', 'Vendor Deleted Successfully!', () => {
            // Refetch data for current page or reload page as in original logic
            window.location.reload(); 
          });
        })
        .catch((err) => {
          hideDialog();
          console.error("Delete vendor failed:", err);
          sweetAlerts('error', 'Failed to delete vendor');
        });
    });
  };

  const handleCellClick = (params) => {
    // Only proceed if the clicked column is NOT the 'id' (action) column
    if (params.field !== 'id') {
      const cellValue = params.value;

      Swal.fire({
          html: `<div style="white-space: pre-wrap;">${cellValue}</div>`,
          showConfirmButton: false,
          customClass: { popup: 'my-custom-size' },
          didOpen: () => {
              const swalPopup = Swal.getPopup();
              if (swalPopup) {
                  swalPopup.style.width = '400px';
              }
          },
      });
    }
  };


  // --- Data Grid Configuration ---

  const rows = vendorData.map((item) => ({
    id: pageIdCounter++, // Optimized ID generation
    vendorId: item?.id,
    vendorName: item?.vendor_name,
    companyName: item?.company_name,
    gstNo: item?.gst_no,
    // Use regex to correctly assign PAN/TAN based on format
    panNo: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(item?.pan_tan) ? item?.pan_tan : '',
    tanNo: /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(item?.pan_tan) ? item?.pan_tan : '',
    billingDetails: item?.billing_details,
    invoiceNumber: item?.invoice_number,
    vendorType: item?.vendor_type,
    tdsAmount: item?.tds_amount
  }));

  const columns = React.useMemo(() => [
    {
      field: 'id',
      headerName: 'ID',
      sortable: false,
      hideable: false,
      filterable: false,
      flex: 100,
      maxWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span>{params.value}</span>
          <NavLink>
            <EditIcon 
              onClick={() => showAddVendorDialog(params.row.vendorId)} 
              style={{ width: '15px', height: "15px", marginLeft: "2px" }} 
              color="action" 
            />
          </NavLink>
          <NavLink>
            <DeleteIcon 
              onClick={() => handleDeleteVendor(params.row.vendorId)} 
              style={{ width: '15px', height: "15px", marginLeft: "2px" }} 
              color="action" 
            />
          </NavLink>
        </div>
      ),
    },
    // Other columns remain similar, using useMemo for stability
    { field: 'vendorName', headerName: 'Vendor Name', sortable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'vendorType', headerName: 'Vendor Type', sortable: false, flex: 250, align: 'center', headerAlign: 'center' },
    { field: 'companyName', headerName: 'Company Name', sortable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'gstNo', headerName: 'GST No.', sortable: false, filterable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'panNo', headerName: 'PAN No.', sortable: false, filterable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'tanNo', headerName: 'TAN No.', sortable: false, filterable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'tdsAmount', headerName: 'TDS Amount', sortable: false, filterable: false, flex: 250, align: 'center', headerAlign: 'center' },
    { field: 'billingDetails', headerName: 'Billing Details', sortable: false, filterable: false, flex: 250, align: 'center', headerAlign: 'center' },
  ], [showAddVendorDialog, handleDeleteVendor]);
  
  // Initialize filter model from URL on first render/mount
  React.useEffect(() => {
    if (filterParam) {
        try {
            const items = JSON.parse(filterParam).map(item => ({
                id: Math.random(), // DataGrid needs an ID for each item
                columnField: item.field,
                operatorValue: 'contains', // Assuming 'contains' is the default operator for text fields
                value: item.value
            }));
            
            const initialFilterModel = { items };
            setQueryOptions({ filterModel: initialFilterModel });
        } catch (e) {
            console.error("Could not parse filter param:", e);
        }
    }
  }, []); // Run only on mount

  // --- Render ---

  return (
    <>
      <div style={{ height: '60px', width: '100%', marginBottom: '16px' }}>
        <Button sx={{ float: 'right' }} variant="contained" onClick={() => showAddVendorDialog()}>
          Add Vendor
        </Button>
      </div>
      <div style={{ height: '100%', width: '100%' }}>

        <DataGridPro 
          sx={{ backgroundColor: '#F6F4FA' }}
          rows={rows}
          columns={columns}
          pageSize={30}
          autoPageSize={true}
          autoHeight={true}
          density='standard'
          filterMode='server'
          onFilterModelChange={onFilterChange}
          hideFooter={true}
          onCellClick={handleCellClick}
          // The component should reflect the filter state from queryOptions for controlled input
          filterModel={queryOptions.filterModel}
        />
        
        <Pagination 
          count={Math.ceil(totalCount / PAGE_LIMIT)} 
          sx={{ mt: 3, float: 'right', color: "#F6F4FA" }} 
          page={currentPage} 
          onChange={handlePageChange} 
        />
      </div>
    </>
  );
}